/* eslint-disable jsx-a11y/anchor-is-valid */
import { FC, useMemo, useState, useEffect, useRef } from 'react';
import clsx from 'clsx'
import { handleNumInput } from '../helpers';
import { times } from 'lodash';

import { useDebounce } from '../../../_metronic/helpers';

type Props = {
  itemCount: number
  currentIndex: number
  displayCount?: number
  onChangeIndex: Function,        
  disabled?: boolean    
  unit?: string 
}

const IndexSelector : FC<Props> = ({ itemCount, currentIndex, displayCount = 5, onChangeIndex, disabled, unit = 'site' }) => {

    const [indexNo, setIndexNo] = useState<string>('0');
    const gotoRef = useRef<HTMLInputElement | null>(null);


    const updateIndex = (new_indexNo: any) => {
      const _newval = parseInt(`${new_indexNo}`);
      if (isNaN(_newval) || (_newval < 1) || (_newval > itemCount)) return;
      setIndexNo(`${_newval}`);
    }            
        
    const debouncedIndexNo = useDebounce(indexNo, 400);

    const { show_cnt, offset, showPrev, showNext, showFirst, showLast } = useMemo(() => {
      const _current = parseInt(indexNo);
    
      let showPrev = itemCount > displayCount && _current > 1;
      let showFirst = false;
      
      let show_cnt = itemCount > displayCount ? displayCount : itemCount;
      let offset = 0;

      let showNext = itemCount > displayCount && _current < itemCount;
      let showLast = itemCount > displayCount;
      
      if (! (_current > displayCount && show_cnt > 0)) {
        return { show_cnt, offset, showPrev, showNext, showFirst, showLast};
      }

      showFirst = _current > 1;
      showNext = _current < itemCount;
      showLast = _current < itemCount;

      offset = Math.floor((_current-1) / show_cnt) * show_cnt;

      // check if show count exceeds last group
      if ((offset + show_cnt) >= itemCount) {
        show_cnt = itemCount - offset;
        showLast = false;
      }

      return { show_cnt, offset, showPrev, showNext, showFirst, showLast};
    }, [displayCount, itemCount, indexNo])

    useEffect(() => {
        let _newindex = parseInt(`${debouncedIndexNo}`) - 1;
        
        if (! (_newindex < itemCount)) {
            _newindex = itemCount - 1;
        }

        if (_newindex < 0) {
            _newindex = 0
        } 

        if (currentIndex !== _newindex) {
            onChangeIndex(_newindex);        
        }
        if (gotoRef.current) {
          gotoRef.current.value = '';
        }
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedIndexNo] );    
    
    useEffect(() => {
        if ((currentIndex + 1).toString() !== indexNo) {
            setIndexNo((currentIndex + 1).toString());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentIndex])


    if (!(itemCount > 1)) {
      return null;
    }

    return (
    <div className='d-flex flex-stack flex-wrap justify-content-between gap-2'>     
      { (itemCount < 5) && 
      <div className="text-muted fs-7">Click button to select {`${unit}`}→</div>
      }
      <div className="align-self-end d-flex flex-row align-items-center gap-2">
        <ul className='pagination fs-10 gap-1'>
          { showFirst && 
          <li
            className={clsx('page-item first', {
            disabled: disabled,                    
          })}
          >
            <a
              className='page-link page-text'
              onClick={() => updateIndex(1)}
              style={{cursor: 'pointer'}}
            >
              1
            </a>
          </li>
          }     
          { showPrev &&
          <li
            className={clsx('page-item previous', {
              disabled: disabled,                    
            })}
          >
            <a
              className={clsx('page-link page-text', {                        
              })}
              onClick={() => updateIndex(parseInt(indexNo) - 1)}
              style={{cursor: 'pointer'}}
            >
              &laquo;
            </a>
          </li>
          }
          {times(show_cnt, (no) => (
          <li
            key={`indexNo${no}`}
            className={clsx('page-item', {
                active: indexNo === (`${no + 1 + offset}`),
                disabled: disabled,                                
            })}
          >
            <a
              className='page-link'
              onClick={() => updateIndex(no + 1 + offset)}
              style={{cursor: 'pointer'}}
            >
              {`${no+1+offset}`}
            </a>
          </li>
          ))
          }  
          { showNext && 
          <li
            className={clsx('page-item next', {
            disabled: disabled,                    
          })}
          >
            <a
              className='page-link page-text'
              onClick={() => updateIndex(parseInt(indexNo) + 1)}
              style={{cursor: 'pointer'}}
            >
              &raquo;
            </a>
          </li>
          } 
          { showLast && 
          <li
            className={clsx('page-item last', {
            disabled: disabled,                    
          })}
          >
            <a
              className='page-link page-text'
              onClick={() => updateIndex(itemCount)}
              style={{cursor: 'pointer'}}
            >
              {itemCount}
            </a>
          </li>
          }     
        </ul>       
        { itemCount > displayCount && 
        <div className="d-flex flex-row align-items-center gap-2 fs-7 ms-4">
          <label>Go To:</label>
          <input 
            ref={gotoRef}
            type="text"                     
            className="form-control form-control-sm border-1 w-50px text-center py-1 fs-7"
            onKeyDown={handleNumInput}
            onChange={(e) => {
              setIndexNo(e.target.value);
            }}
          />
        </div>
        }                                 
      </div>             
    </div>        
    )
}

export {IndexSelector}
